import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, CheckCircle } from "@phosphor-icons/react";

const zones = [
    {
        id: "idf",
        name: "Île-de-France",
        depts: "75 · 77 · 78 · 91 · 92 · 93 · 94 · 95",
        cx: 130,
        cy: 200,
        r: 50,
        color: "#00AFFF",
        delay: 0,
        cities: ["Paris", "Versailles", "Créteil"],
    },
    {
        id: "oise",
        name: "Oise (60)",
        depts: "Compiègne · Beauvais · Senlis",
        cx: 195,
        cy: 110,
        r: 35,
        color: "#22C55E",
        delay: 0.15,
        cities: ["Beauvais", "Compiègne", "Creil"],
    },
    {
        id: "aisne",
        name: "Aisne (02)",
        depts: "Saint-Quentin · Soissons · Laon",
        cx: 285,
        cy: 90,
        r: 38,
        color: "#F59E0B",
        delay: 0.3,
        cities: ["Laon", "Saint-Quentin", "Soissons"],
    },
];

export default function ZoneMap() {
    const [active, setActive] = useState("idf");

    return (
        <section id="zones" data-testid="zones-section" className="relative py-24 lg:py-28 bg-lumos-bg overflow-hidden">
            <div className="absolute inset-0 bg-grid opacity-50" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <span className="eyebrow justify-center">Zones d&apos;intervention</span>
                    <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-lumos-ink leading-[1.05]">
                        Présent là où<br />
                        <span className="font-serif-display text-lumos-blue">vos projets prennent vie.</span>
                    </h2>
                </div>

                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    {/* MAP */}
                    <div className="lg:col-span-7">
                        <div className="relative rounded-3xl bg-white border border-lumos-border p-6 lg:p-10 shadow-[0_30px_80px_-30px_rgba(10,17,40,0.18)]">
                            <svg
                                viewBox="0 0 400 300"
                                className="w-full h-auto"
                                role="img"
                                aria-label="Carte des zones d&apos;intervention LUMOS : Île-de-France, Oise, Aisne"
                            >
                                <defs>
                                    {zones.map((z) => (
                                        <radialGradient key={z.id} id={`grad-${z.id}`}>
                                            <stop offset="0%" stopColor={z.color} stopOpacity="0.35" />
                                            <stop offset="100%" stopColor={z.color} stopOpacity="0" />
                                        </radialGradient>
                                    ))}
                                </defs>

                                {/* abstract France hint */}
                                <path
                                    d="M50 200 Q60 100 150 80 Q260 60 340 100 Q360 180 320 240 Q220 280 130 270 Q60 250 50 200 Z"
                                    fill="rgba(10,17,40,0.04)"
                                    stroke="rgba(10,17,40,0.08)"
                                    strokeWidth="1.5"
                                />

                                {zones.map((z) => (
                                    <motion.g
                                        key={z.id}
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.7, delay: z.delay }}
                                        onMouseEnter={() => setActive(z.id)}
                                        onClick={() => setActive(z.id)}
                                        className="zone-shape cursor-pointer"
                                        data-testid={`zone-${z.id}`}
                                    >
                                        {/* halo */}
                                        <circle cx={z.cx} cy={z.cy} r={z.r + 25} fill={`url(#grad-${z.id})`} />
                                        {/* main blob */}
                                        <circle
                                            cx={z.cx}
                                            cy={z.cy}
                                            r={z.r}
                                            fill={z.color}
                                            fillOpacity={active === z.id ? 0.35 : 0.18}
                                            stroke={z.color}
                                            strokeWidth={active === z.id ? 2.5 : 1.5}
                                        />
                                        {/* pulse dot */}
                                        <circle cx={z.cx} cy={z.cy} r={5} fill={z.color}>
                                            <animate attributeName="r" values="5;9;5" dur="2.2s" repeatCount="indefinite" />
                                        </circle>
                                        <circle cx={z.cx} cy={z.cy} r={3} fill="white" />
                                        <text
                                            x={z.cx}
                                            y={z.cy + z.r + 18}
                                            textAnchor="middle"
                                            className="fill-lumos-ink font-display font-bold"
                                            fontSize="14"
                                        >
                                            {z.name}
                                        </text>
                                    </motion.g>
                                ))}
                            </svg>

                            <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-lumos-blue/10 border border-lumos-blue/25 text-lumos-blue text-[11px] font-bold tracking-widest uppercase">
                                <span className="w-1.5 h-1.5 rounded-full bg-lumos-blue animate-pulse" />
                                Carte interactive
                            </div>
                        </div>
                    </div>

                    {/* DETAILS */}
                    <div className="lg:col-span-5 space-y-3">
                        {zones.map((z) => {
                            const isActive = active === z.id;
                            return (
                                <motion.button
                                    type="button"
                                    key={z.id}
                                    onMouseEnter={() => setActive(z.id)}
                                    onFocus={() => setActive(z.id)}
                                    onClick={() => setActive(z.id)}
                                    data-testid={`zone-detail-${z.id}`}
                                    className={`w-full text-left rounded-2xl border p-5 transition-all duration-400 ${
                                        isActive
                                            ? "bg-white border-lumos-blue shadow-[0_18px_50px_-20px_rgba(0,175,255,0.45)]"
                                            : "bg-white/60 border-lumos-border hover:bg-white"
                                    }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div
                                            className="w-12 h-12 rounded-xl grid place-items-center flex-shrink-0"
                                            style={{ background: `${z.color}1f`, color: z.color }}
                                        >
                                            <MapPin size={22} weight="fill" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display font-bold text-xl text-lumos-ink">{z.name}</div>
                                            <div className="text-sm text-lumos-muted mt-0.5">{z.depts}</div>
                                            <div className="flex flex-wrap gap-2 mt-3">
                                                {z.cities.map((c) => (
                                                    <span
                                                        key={c}
                                                        className="px-2.5 py-1 rounded-full text-[11px] font-semibold border"
                                                        style={{
                                                            borderColor: `${z.color}40`,
                                                            background: `${z.color}10`,
                                                            color: z.color,
                                                        }}
                                                    >
                                                        {c}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <CheckCircle
                                            size={22}
                                            weight="fill"
                                            className={`transition-opacity ${isActive ? "opacity-100" : "opacity-30"}`}
                                            style={{ color: z.color }}
                                        />
                                    </div>
                                </motion.button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
